import React, { useCallback } from 'react';
import { useFocusEffect } from 'expo-router';

import { useSelector } from '@xstate5/react';

import { Modal } from '@sg/garnish';

import {
  GroupOrderConfirmationContent,
  useGroupOrderingContent,
} from '@order/features-group-ordering';

import { useGlobalAppState } from '../../../../context/GlobalAppState';
import { useConflictReview } from '../../hooks/useConflictReview';

// ────────────────────────────────────────────────────────────────────────────

export const GroupOrderReorderConfirmationModal = () => {
  const { reorderMachineRef } = useGlobalAppState();
  const state = useSelector(reorderMachineRef, (snapshot) => snapshot);
  const conflictReview = useConflictReview();
  const { mappedContent } = useGroupOrderingContent();

  // ─── Derived ─────────────────────────────────────────────────────────────────

  const { groupOrderReorderRequest } = state.context;

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    reorderMachineRef.send({ type: 'CANCEL_GROUP_ORDER_REORDER_CONFIRMATION' });
  }, [reorderMachineRef]);

  const handleConfirm = useCallback(() => {
    if (!groupOrderReorderRequest) return;

    reorderMachineRef.send({ type: 'CONFIRM_GROUP_ORDER_REORDER' });

    void conflictReview(groupOrderReorderRequest.event, {
      isGroupOrderReorderConfirmed: true,
      reorderStep: groupOrderReorderRequest.originStep,
    });
  }, [conflictReview, groupOrderReorderRequest, reorderMachineRef]);

  const handleScreenFocus = useCallback(() => {
    return () => {
      const pendingReorderRequest =
        reorderMachineRef.getSnapshot()?.context.groupOrderReorderRequest;

      if (!pendingReorderRequest) return;

      reorderMachineRef.send({ type: 'CANCEL_GROUP_ORDER_REORDER_CONFIRMATION' });
    };
  }, [reorderMachineRef]);

  // ─── Effects ───────────────────────────────────────────────────────────────────

  useFocusEffect(handleScreenFocus);

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <Modal
      visible={state.matches('confirmingGroupOrderReorder')}
      size="small"
      withSafeAreaView={false}
      accessibilityLabel={mappedContent.reorderCartModalTitle}
      onRequestClose={handleClose}
    >
      <GroupOrderConfirmationContent
        title={mappedContent.reorderCartModalTitle}
        body={mappedContent.reorderCartModalBody}
        dismissButtonLabel={mappedContent.reorderCartModalDismissButtonLabel}
        confirmButtonLabel={mappedContent.reorderCartModalConfirmButtonLabel}
        confirmButtonPalette="primary"
        isPerformingAction={false}
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
    </Modal>
  );
};
